import axios from 'axios';
import { Auth0Client } from '@auth0/auth0-spa-js';

// LINKID
import linkIdConfiguration from 'config/linkId';

// HELPERS
import { removeKey } from 'helpers/localstorage';

// CONSTANT
import { ApiAuth } from 'constant';

class Auth0Service {
  private readonly auth0?: Auth0Client;

  // Last sid read from the ID token. The refresh-token grant renews the access token without
  // re-issuing the ID token, so `getIdTokenClaims` can come back empty on a still-authenticated
  // tab — this keeps the SessionId header available for that logout (BU-21057).
  private cachedSessionId?: string;

  constructor() {
    if (!navigator.userAgent.includes('jsdom')) {
      this.auth0 = new Auth0Client({
        domain: linkIdConfiguration.domain,
        client_id: linkIdConfiguration.clientId,
        audience: linkIdConfiguration.audience,
        scope: linkIdConfiguration.scope,
        redirect_uri: linkIdConfiguration.redirectUri,
        cacheLocation: linkIdConfiguration.cacheLocation,
        useRefreshTokens: linkIdConfiguration.useRefreshTokens
      });
    }
  }

  async generateNewToken(ignoreCache: boolean): Promise<{ token: string }> {
    try {
      const token = await this.auth0!.getTokenSilently({ ignoreCache });

      return {
        token
      };
    } catch (error) {
      return {
        token: ''
      };
    }
  }

  // gateway-service (BU-21057) - the LinkPass ID token's `sid` claim, sent as the SessionId
  // header on every authenticated request so the gateway can enforce a single active session
  // per user. Stable for the life of the LinkPass session; changes only on a fresh login.
  async getSessionId(): Promise<string | undefined> {
    try {
      const claims = await this.auth0!.getIdTokenClaims();

      if (claims?.sid) {
        this.cachedSessionId = claims.sid;
      }
    } catch (error) {
      // fall through to the cached value below
    }

    return this.cachedSessionId;
  }

  async logOut(): Promise<void> {
    removeKey('@latestDateShowConfirmMHQDialog');

    await this.notifyGatewayLogout();

    // Cleared after the gateway call, which needs it, so no stale sid reaches the next login.
    this.cachedSessionId = undefined;

    return this.auth0!.logout({ returnTo: window.location.origin });
  }

  // Best-effort teardown of the gateway-service session record and cached B3 token. Never blocks
  // the local logout: on failure (or no token available) the record is simply left to expire on
  // its TTL, same as if the tab had been closed.
  private async notifyGatewayLogout(): Promise<void> {
    try {
      const [token, sid] = await Promise.all([
        this.auth0!.getTokenSilently({ ignoreCache: false }).catch(() => undefined),
        this.getSessionId()
      ]);

      if (!token) return;

      await axios.post(`${process.env.REACT_APP_API_URL}${ApiAuth.Logout}`, null, {
        headers: {
          authorization: `Bearer ${token}`,
          ...(sid ? { SessionId: sid } : {})
        }
      });
    } catch (error) {
      // ignore - logout must always proceed locally regardless of gateway teardown outcome
    }
  }

  async logInWithRedirect(router: string): Promise<void> {
    return this.auth0!.loginWithRedirect({ appState: { returnTo: router } });
  }
}

export const authService = new Auth0Service();
